import { useEffect, useState } from "react";
import { Plus, ClipboardList, ChevronRight, Pencil, Trash2, GitBranch } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { formatApiError, formatShort } from "../lib/format";
import ProcessTimeline from "../components/ProcessTimeline";

const KINDS = ["Renovação de CRAF","Guia de Tráfego","Porte de Trânsito","Registro","Transferência de Registro","Aquisição","Outro"];
const STATUSES = [
  { v: "Aberto", c: "blue" },
  { v: "Em análise", c: "blue" },
  { v: "Documentos pendentes", c: "amber" },
  { v: "Pronto para emissão", c: "green" },
  { v: "Concluído", c: "green" },
  { v: "Cancelado", c: "amber" },
];

function ProcessDialog({ open, initial, clients, weapons, onClose, onSaved }) {
  const [form, setForm] = useState({ client_id:"", weapon_id:"", kind:KINDS[0], status:"Aberto", progress:0, due_date:"", notes:"" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setForm({
      client_id: initial?.client_id || clients[0]?.id || "",
      weapon_id: initial?.weapon_id || "",
      kind: initial?.kind || KINDS[0],
      status: initial?.status || "Aberto",
      progress: initial?.progress ?? 0,
      due_date: initial?.due_date || "",
      notes: initial?.notes || "",
    });
  }, [open, initial, clients]);
  if (!open) return null;
  const isEdit = !!initial?.id;
  const clientWeapons = weapons.filter(w => w.client_id === form.client_id);

  const save = async () => {
    if (!form.client_id || !form.kind) { toast.error("Preencha cliente e tipo"); return; }
    setBusy(true);
    try {
      const payload = { ...form, weapon_id: form.weapon_id || null, progress: Number(form.progress) };
      if (isEdit) await api.patch(`/processes/${initial.id}`, {
        status: form.status, progress: payload.progress, due_date: form.due_date, notes: form.notes,
      });
      else await api.post("/processes", payload);
      toast.success(isEdit ? "Processo atualizado" : "Processo criado");
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal wide" data-testid="process-dialog" onClick={e=>e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} data-testid="close-process-dialog"><span aria-hidden>×</span></button>
        <span className="eyebrow">{isEdit ? "EDITAR PROCESSO" : "NOVO PROCESSO"}</span>
        <h2>Processo de regularização</h2>
        <p>Acompanhe cada etapa até a emissão.</p>
        <div className="form-grid">
          <label>Cliente
            <select data-testid="process-client-select" value={form.client_id} onChange={e=>setForm({...form,client_id:e.target.value, weapon_id:""})} disabled={isEdit}>
              <option value="">Selecione...</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <label>Arma vinculada (opcional)
            <select data-testid="process-weapon-select" value={form.weapon_id} onChange={e=>setForm({...form,weapon_id:e.target.value})} disabled={isEdit}>
              <option value="">— sem vínculo —</option>
              {clientWeapons.map(w => <option key={w.id} value={w.id}>{w.model}</option>)}
            </select>
          </label>
          <label>Tipo
            <select data-testid="process-kind-select" value={form.kind} onChange={e=>setForm({...form,kind:e.target.value})} disabled={isEdit}>
              {KINDS.map(k=><option key={k}>{k}</option>)}
            </select>
          </label>
          <label>Status
            <select data-testid="process-status-select" value={form.status} onChange={e=>setForm({...form,status:e.target.value})}>
              {STATUSES.map(s=><option key={s.v}>{s.v}</option>)}
            </select>
          </label>
          <label>Prazo<input data-testid="process-due-input" type="date" value={form.due_date || ""} onChange={e=>setForm({...form,due_date:e.target.value})}/></label>
          <label>Progresso (%)<input data-testid="process-progress-input" type="number" min={0} max={100} value={form.progress} onChange={e=>setForm({...form,progress:e.target.value})}/></label>
          <label className="span-2">Observações<textarea data-testid="process-notes-input" rows={2} value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})}/></label>
        </div>
        <button data-testid="save-process-button" className="primary-button full" disabled={busy} onClick={save}>{busy ? "Salvando..." : (isEdit ? "Salvar" : "Criar processo")}</button>
      </div>
    </div>
  );
}

export default function Processes() {
  const [processes, setProcesses] = useState([]);
  const [clients, setClients] = useState([]);
  const [weapons, setWeapons] = useState([]);
  const [dialog, setDialog] = useState({ open:false, initial:null });
  const [timelineId, setTimelineId] = useState(null);

  const load = async () => {
    try {
      const [p,c,w] = await Promise.all([api.get("/processes"), api.get("/clients"), api.get("/weapons")]);
      setProcesses(p.data); setClients(c.data); setWeapons(w.data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm("Excluir este processo?")) return;
    try { await api.delete(`/processes/${id}`); toast.success("Processo removido"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <>
      <div className="page-heading">
        <div>
          <span className="eyebrow">FLUXO DE REGULARIZAÇÃO</span>
          <h1>Processos</h1>
          <p>Renovações, guias e transferências no seu ritmo.</p>
        </div>
        <button data-testid="new-process-button" className="primary-button" onClick={()=>setDialog({open:true, initial:null})}><Plus size={17}/> Novo processo</button>
      </div>
      <section className="panel full-panel">
        {processes.map((p,i) => {
          const stColor = STATUSES.find(s=>s.v===p.status)?.c || "blue";
          return (
            <div className="process-row" data-testid={`process-row-${p.id}`} key={p.id}>
              <div className="process-icon"><ClipboardList size={18}/></div>
              <div className="process-main">
                <b>{p.kind}</b>
                <span>{p.client_name} {p.due_date && ` · Prazo ${formatShort(p.due_date)}`}</span>
                <div className="progress"><i style={{width:`${p.progress||0}%`}} className={stColor}/></div>
              </div>
              <strong>{p.progress||0}%</strong>
              <label className={`process-status ${stColor}`}>{p.status}</label>
              <div className="row-actions">
                <button data-testid={`timeline-process-${p.id}`} className="icon-mini" title="Timeline" onClick={()=>setTimelineId(p.id)}><GitBranch size={14}/></button>
                <button data-testid={`edit-process-${p.id}`} className="icon-mini" onClick={()=>setDialog({open:true, initial:p})}><Pencil size={14}/></button>
                <button data-testid={`delete-process-${p.id}`} className="icon-mini danger" onClick={()=>remove(p.id)}><Trash2 size={14}/></button>
              </div>
            </div>
          );
        })}
        {!processes.length && <div className="empty-inline">Nenhum processo em andamento.</div>}
      </section>
      <ProcessDialog open={dialog.open} initial={dialog.initial} clients={clients} weapons={weapons}
        onClose={()=>setDialog({open:false, initial:null})} onSaved={load}/>
      {timelineId && (
        <ProcessTimeline processId={timelineId} onClose={()=>setTimelineId(null)} onChanged={load}/>
      )}
    </>
  );
}
